<!-- ライブチャット -->
<script setup lang="ts">
  import { DynamicScroller, DynamicScrollerItem } from "~/customModules/vue-virtual-scroller.esm.js";
  import "~/customModules/vue-virtual-scroller.css";
  import { useLiveChatStore } from "~/stores/LiveChatStore";
  import { LiveChatMessage } from "~/entities/LiveChatMessage";
  import { useVideoStateStore } from "~/stores/VideoStateStore";
  import { useUserStore } from "~/stores/UserStore";
  import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
  import { useSnackBarStore } from "~/stores/SnackBarStore";

  // ユーザー情報
  const userStore = useUserStore();

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  // ビデオ周りは複雑なレイアウトなのでステート管理にpiniaを使用する
  const videoStateStore = useVideoStateStore();

  interface Props {
    liveId: string;
    height: string | number; // h-100設定だと何故かうまく動かないのでheightを渡す
    readonly: boolean;
    isArchive: boolean; // アーカイブ動画再生中かどうか。動画の現在時刻を利用してフィルターする
    isOwner?: boolean;
    ownerId?: string;
    showBackButton?: boolean;
  }
  const props = withDefaults(defineProps<Props>(), {
    liveId: "test",
    height: undefined,
    readonly: true,
    isArchive: false,
    isOwner: false,
    ownerId: "",
    showBackButton: false,
  });

  interface Emits {
    (event: "clickClose"): void; // 閉じるボタン押下イベント(SP版のみ)
  }
  const emit = defineEmits<Emits>();

  const liveChatStore = useLiveChatStore();

  const message = ref<string>(""); // 入力メッセージ
  // FIXME any 使いたくないが warning 消せないので・・
  // const scrollArea = ref<InstanceType<typeof DynamicScroller> | null>(null);
  const scrollArea = ref<InstanceType<any> | null>(null);
  const chatMessages = ref<LiveChatMessage[]>([]); // チャットメッセージリスト。フィルタリングが必要なので保持しておく
  const chatUserIdList = ref<string[]>([]); // 重複を除いたUserIDのリスト。毎回リストを作成するのは負荷が高そうなのでメッセージが変化した際に取得して保存しておく

  const fetch = async () => {
    liveChatStore.ownerId = props.ownerId;
    await liveChatStore.fetch(props.liveId);
    if (!props.readonly) {
      await liveChatStore.startObserve(props.liveId);
    }
  };

  const insert = async (message: LiveChatMessage): Promise<void> => {
    if (!props.readonly) {
      await liveChatStore.insert(props.liveId, message);
    }
  };

  onMounted(async () => {
    await fetch();
    if (!props.readonly) {
      await startChat();
    }
  });

  onUnmounted(() => {
    liveChatStore.stopObserve();
  });

  const loading = computed<boolean>(() => liveChatStore.loading);

  const startChat = async () => {
    if (props.readonly) {
      return;
    }
    if (!userStore.user) {
      return;
    }

    if (props.isOwner) {
      // オーナーは入室しましたメッセージを表示しない
      return;
    }

    if (liveChatStore.userIdList?.includes(userStore.user.userId)) {
      // すでに入室済みの場合は入室しましたメッセージは表示しない
      return;
    }

    const data = new LiveChatMessage(
      "",
      userStore.user.userId,
      userStore.user.userImage,
      userStore.user.userName,
      `${userStore.user.userName}が入室しました`,
      "access",
      "0",
      null,
    );
    try {
      await insert(data);
    } catch {
      sentryErrorLog("ライブコメント入室メッセージ送信失敗");
    }
  };

  const textInput = ref<HTMLInputElement | null>(null);

  /*
    日本語変換時のエンターキー対応
    https://qiita.com/ledsun/items/31e43a97413dd3c8e38e
    keydownではなくkeypressを使う
  */
  const onShiftEnterKey = (_e: any) => {
    // shift+enter でのみ送信（トーク同様）
    onClickSend();
  };

  const onClickSend = async () => {
    if (message.value.length === 0) {
      return;
    }
    if (!userStore.user) {
      // ログインしてない場合はエラーを出す
      videoStateStore.gotoLogin = true;
      return;
    }
    // 投稿禁止チェック
    if (userStore.isPostProhibited()) {
      return;
    }

    const data = new LiveChatMessage(
      "",
      userStore.user.userId,
      userStore.user.userImage,
      userStore.user.userName,
      message.value,
      "message",
      "0",
      null,
    );
    try {
      await insert(data);
      message.value = "";
      textInput.value!.blur();
    } catch (error: any) {
      sentryErrorLog(`ライブコメントメッセージ送信失敗: ${error}`);
      snackBarStore.setError("送信に失敗しました");
    }
  };

  const filterMessage = () => {
    if (props.isArchive) {
      if (liveChatStore.messages) {
        chatMessages.value = liveChatStore.messages.filter((item: LiveChatMessage) => {
          return item.createdAt.getTime() <= videoStateStore.currentDateTime.getTime();
        });
      }
    } else {
      chatMessages.value = liveChatStore.messages ?? [];
    }
    chatUserIdList.value = liveChatStore.getUserIdList(chatMessages.value);
  };

  watch(
    () => {
      return videoStateStore.currentDateTime;
    },
    () => {
      // アーカイブ再生時には動画の時間が変わったときにメッセージをフィルタリングする
      if (props.isArchive) {
        filterMessage();
      }
    },
  );

  watch(
    () => {
      return liveChatStore.messages?.length;
    },
    () => {
      filterMessage();
    },
  );

  let initScroll = false;
  let scrollingToBottom = false;

  watch(
    () => {
      return chatMessages.value?.length;
    },
    () => {
      if (scrollArea.value) {
        const { scrollTop, offsetHeight, scrollHeight } = scrollArea.value.$el;

        // スクロール位置が最下部か判別。100pxほどマージンを持たせて判別する。
        if (scrollTop + offsetHeight >= scrollHeight - 100) {
          // 初回読み込み時には最下部に移動する
          // vue更新前に現在の状態を判別しフラグを立てておく。この時点ではまだscrollAreaは更新されていないので最下部までスクロールできない。
          scrollToBottom();
        }
      }
    },
  );
  watch(
    () => {
      return chatMessages.value?.length;
    },
    () => {
      if (scrollArea.value) {
        if (!initScroll) {
          // 初回読み込み時には最下部に移動する
          scrollToBottom();
        }
        initScroll = true;
      }
    },
    { flush: "post" }, // このオプション指定でvueコンポーネントが更新された後に呼ばれる
  );

  const scrollToBottom = () => {
    if (!scrollingToBottom) {
      scrollToPosition(999999999);
      scrollingToBottom = true;
    }
  };

  const scrollToPosition = (position: number) => {
    if (scrollArea.value) {
      scrollArea.value.$el.scrollTop = position;
      // アイテムの高さが可変のスクローラーなので一回の変更では上手く移動できない。そのための苦肉の策として何度もスクロール位置変更処理をしている。
      requestAnimationFrame(() => {
        if (scrollArea.value) scrollArea.value.$el.scrollTop = position;
        setTimeout(() => {
          if (scrollArea.value) scrollArea.value.$el.scrollTop = position;
        }, 50);
        setTimeout(() => {
          if (scrollArea.value) scrollArea.value.$el.scrollTop = position;
          scrollingToBottom = false;
        }, 200);
      });
    }
  };

  // コメント数
  const messageCount = computed<number>(() => liveChatStore.messageCount(chatMessages.value));

  // 来場者数
  const userCount = computed<number>(() => liveChatStore.userCountFromUserIdList(chatUserIdList.value));

  const scrollHeight = computed<string>(() => {
    if (props.readonly) {
      return "height: calc(100% - 50px)";
    }
    // 最後の -60px は下記での調整
    // https://fincs.backlog.com/view/FINCS-4032
    return "height: calc(100% - 40px - 50px - 60px)";
  });

  // 閉じるボタン押下
  const clickClose = () => {
    emit("clickClose");
  };

  defineExpose({
    fetch,
    loading,
  });
</script>

<template>
  <v-sheet class="d-flex flex-column overflow-y-hidden" :height="height" color="monochrome-05">
    <v-sheet class="mx-4" :height="50" color="transparent">
      <div class="my-3 d-flex align-end" style="vertical-align: baseline; position: relative">
        <span class="text-count-title">総来場者数:</span
        ><span class="text-count ml-1">{{ numberFormat(userCount) }}</span>
        <span class="text-count-title ml-4">コメント数:</span
        ><span class="text-count ml-1">{{ numberFormat(messageCount) }}</span>
        <v-btn v-if="showBackButton" class="close-button" @click="clickClose">
          <v-icon size="24px" color="white" icon="$mdiClose"></v-icon>
        </v-btn>
      </div>
      <v-sheet width="100%" :height="1" color="rgba(255,255,255,0.1)" />
    </v-sheet>
    <template v-if="loading">
      <AtomsLoadingSkeleton class="rounded" :width="240" :height="20" />
    </template>
    <template v-else>
      <DynamicScroller
        ref="scrollArea"
        :items="chatMessages"
        :min-item-size="54"
        class="scroller"
        :style="scrollHeight"
      >
        <template #default="{ item, index, active }">
          <DynamicScrollerItem :item="item" :active="active" :size-dependencies="[item.text]" :data-index="index">
            <AtomsLiveChatMessage :message="item" :loading="false" />
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
      <v-sheet
        v-if="!props.readonly"
        class="rounded-pill mx-2 mt-2 mb-4"
        color="rgba(255,255,255,0.5)"
        style="max-height: 40px"
      >
        <v-textarea
          ref="textInput"
          v-model="message"
          variant="solo-filled"
          placeholder="コメントを書く...."
          bg-color="#666666"
          :rows="1"
          :max-rows="4"
          hide-details
          class="text-chat-input"
          auto-grow
          flat
          :autofocus="false"
          rounded="xl"
          density="compact"
          @keypress.shift.enter.prevent="onShiftEnterKey"
        >
          <template #append-inner>
            <v-sheet class="chat-send-button" color="transparent" data-testid="send-chat-message" @click="onClickSend">
              <AtomsSvgIcon :width="18" :height="18" :img-path="`assets/images/icon/chat-message-send.svg`" />
            </v-sheet>
          </template>
        </v-textarea>
      </v-sheet>
    </template>
  </v-sheet>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  :deep(.text-chat-input .v-input__control .v-field .v-field__field input) {
    color: white;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.8em;
    letter-spacing: 0.0071428571em !important;
    text-transform: none !important;
    word-break: break-all;
  }
  :deep(.text-chat-input .v-input__control .v-field .v-field__append-inner) {
    color: white;
  }
  :deep(.v-field__overlay) {
    background-color: transparent !important;
  }
  .chat-send-button {
    padding: 6px;
    cursor: pointer;
  }
  .text-count-title {
    color: white;
    font-size: 14px;
    font-weight: 400;
    line-height: 16px; // レイアウトが崩れるので個別対応
    letter-spacing: 0em; // レイアウトが崩れるので個別対応
    @include display_sp {
      font-size: 12px;
    }
  }
  .text-count {
    color: white;
    font-size: 18px;
    font-weight: 700;
    line-height: 18px; // レイアウトが崩れるので個別対応
    letter-spacing: 0em; // レイアウトが崩れるので個別対応
  }

  .close-button {
    width: 24px;
    min-width: 24px;
    height: 24px;
    min-height: 24px;
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translate(0%, -50%);
    -webkit-transform: translate(0%, -50%);
    -ms-transform: translate(0%, -50%);
  }
</style>
